/**
 * DI-2-2 §2.3 — in-set duplicates and same-name pairs.
 *
 * DI-D4 c1: the key is the PAYLOAD hash (`payloadSha256Hex`, SHA-256 of the
 * scan data — unchanged by a metadata rewrite), never the file name and never
 * the file MD5. Rows with an equal payload hash form a duplicate group over the
 * whole drop; the FIRST BY RELATIVE PATH (code-point order) is kept, the others
 * are marked `droppedAsDuplicateOf` — shown, excluded from grouping and from
 * the later round trip, never deleted from the store (DI-D10, `01` §6.6:
 * "dropped from the set visibly").
 *
 * DI-D4 c2: equal basename + different payload hashes = different images —
 * every row kept, every row flagged with its twins (DJI counters restart per
 * folder and per day).
 *
 * A row without a payload hash (not hashed yet, or a file with no scan) is
 * neither a duplicate nor a same-name twin: there is nothing to compare.
 * The size + first-MB pre-filter of `01` §6.6 is deliberately NOT built
 * (story §2.3): DI-2-1 hashes every file fully anyway.
 */
import type { DuplicateGroup, GroupRow, SameNameGroup } from "./types.js";

function byPath(a: GroupRow, b: GroupRow): number {
  if (a.relativePath !== b.relativePath)
    return a.relativePath < b.relativePath ? -1 : 1;
  return a.index - b.index;
}

function payloadOf(r: GroupRow): string | null {
  return r.hashes?.payloadSha256Hex ?? null;
}

export function findDuplicates(rows: readonly GroupRow[]): DuplicateGroup[] {
  const byHash = new Map<string, GroupRow[]>();
  for (const r of rows) {
    const h = payloadOf(r);
    if (h === null) continue;
    const list = byHash.get(h);
    if (list) list.push(r);
    else byHash.set(h, [r]);
  }
  const groups: DuplicateGroup[] = [];
  for (const [payloadSha256Hex, list] of byHash) {
    if (list.length < 2) continue;
    const sorted = list.slice().sort(byPath);
    const [kept, ...dropped] = sorted as [GroupRow, ...GroupRow[]];
    groups.push({
      payloadSha256Hex,
      keptIndex: kept.index,
      droppedIndexes: dropped.map((d) => d.index),
    });
  }
  const pathOf = new Map(rows.map((r) => [r.index, r.relativePath]));
  groups.sort((a, b) => {
    const pa = pathOf.get(a.keptIndex) ?? "";
    const pb = pathOf.get(b.keptIndex) ?? "";
    if (pa !== pb) return pa < pb ? -1 : 1;
    return a.keptIndex - b.keptIndex;
  });
  return groups;
}

export function findSameNameDifferentPayload(
  rows: readonly GroupRow[],
): SameNameGroup[] {
  const byName = new Map<string, GroupRow[]>();
  for (const r of rows) {
    if (payloadOf(r) === null) continue;
    const list = byName.get(r.name);
    if (list) list.push(r);
    else byName.set(r.name, [r]);
  }
  const groups: SameNameGroup[] = [];
  for (const [name, list] of byName) {
    const payloads = new Set(list.map(payloadOf));
    if (payloads.size < 2) continue;
    groups.push({
      name,
      indexes: list
        .slice()
        .sort(byPath)
        .map((r) => r.index),
    });
  }
  groups.sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1));
  return groups;
}
